import type { Ref } from 'vue'
import { onScopeDispose, ref, watch } from 'vue'

export function useBottomSheetDrag(
  sheet: Ref<HTMLElement | null>,
  enabled: Ref<boolean>,
  close: () => void,
  readOffset?: () => number,
) {
  const offset = ref(0)
  const dragging = ref(false)
  let pointerId: number | undefined
  let captureTarget: HTMLElement | undefined
  let startY = 0
  let samples: { y: number; time: number }[] = []

  function release() {
    const id = pointerId
    pointerId = undefined
    dragging.value = false
    if (id !== undefined && captureTarget?.hasPointerCapture(id)) {
      captureTarget.releasePointerCapture(id)
    }
    captureTarget = undefined
  }

  function reset() {
    release()
    offset.value = 0
    samples = []
  }

  function onPointerDown(event: PointerEvent) {
    if (!enabled.value || pointerId !== undefined || !event.isPrimary || event.button !== 0) {
      return
    }
    if (
      event.target instanceof Element
      && event.target.closest('button, a, input, textarea, select, [contenteditable], [data-bottom-sheet-no-drag]')
    ) {
      return
    }
    pointerId = event.pointerId
    captureTarget = event.currentTarget as HTMLElement
    // A new gesture can interrupt the spring returning the sheet to rest.
    const currentOffset = Math.max(0, readOffset?.() ?? offset.value)
    dragging.value = true
    offset.value = currentOffset
    startY = event.clientY - currentOffset
    samples = [{ y: event.clientY, time: event.timeStamp }]
    captureTarget.setPointerCapture(pointerId)
  }

  function onPointerMove(event: PointerEvent) {
    if (event.pointerId !== pointerId) {
      return
    }
    offset.value = Math.max(0, event.clientY - startY)
    samples = samples.filter(sample => event.timeStamp - sample.time <= 100)
    samples.push({ y: event.clientY, time: event.timeStamp })
  }

  function onPointerUp(event: PointerEvent) {
    if (event.pointerId !== pointerId) {
      return
    }
    onPointerMove(event)
    const first = samples[0]!
    const elapsed = event.timeStamp - first.time
    const velocity = elapsed > 0 ? (event.clientY - first.y) / elapsed : 0
    const distanceThreshold = Math.min(160, (sheet.value?.getBoundingClientRect().height ?? 0) * 0.35)
    const shouldClose = offset.value > distanceThreshold || (offset.value >= 30 && velocity > 0.5)
    release()
    if (shouldClose) {
      close()
    } else {
      offset.value = 0
    }
  }

  function onPointerCancel(event: PointerEvent) {
    if (event.pointerId === pointerId) {
      reset()
    }
  }

  watch(enabled, value => {
    if (!value && dragging.value) {
      reset()
    }
  })
  onScopeDispose(reset)

  return { offset, dragging, reset, onPointerDown, onPointerMove, onPointerUp, onPointerCancel }
}
